/* eslint-disable react/prop-types */
import { Link } from "react-router-dom";
import {
  Phone, MessageSquare, Mail, CalendarPlus, Star, Flame,
  Building2, MapPin, ExternalLink,
} from "lucide-react";
import { LEAD_PHASE_GROUPS, PRICE_BRACKET_LABELS } from "../../../lib/enums";

/**
 * LeadHeroSection — Phase 7 Sub-iter 7B.
 *
 * The Overview tab body for the Lead detail page.  Hero card (title +
 * customer link + heat badge + phase chip) + quick actions + phase
 * picker + meta strip + notes + promote-to-customer prompt.
 */

const ALL_PHASES = LEAD_PHASE_GROUPS.flatMap((g) => g.phases);
const phaseMeta = (k) => ALL_PHASES.find((p) => p.key === k) || { label: k, color: "#647087" };

function HeatBadge({ score }) {
  const s = Number(score) || 0;
  const color = s >= 70 ? "#F43F5E" : s >= 40 ? "#F59E0B" : "#647087";
  return (
    <div
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[11px] font-bold uppercase tracking-wider"
      style={{ borderColor: `${color}55`, background: `${color}1a`, color }}
      data-testid="lead-heat-badge"
      title={`Heat score: ${s}/100`}
    >
      <Flame className="w-3.5 h-3.5" /> {s}
    </div>
  );
}

function ActionTile({ icon: Icon, label, onClick, href, color = "#1FB6C1", testid }) {
  const inner = (
    <>
      <Icon className="w-4 h-4" style={{ color }} />
      <span>{label}</span>
    </>
  );
  const cls = "hv-btn hv-btn-ghost text-xs gap-2 inline-flex items-center";
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={cls} data-testid={testid}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls} data-testid={testid}>
      {inner}
    </button>
  );
}

export default function LeadHeroSection({
  lead, customer, phone, email, phaseSaving,
  onUpdatePhase, onOpenMeeting, onPromoteToCustomer,
}) {
  const meta = phaseMeta(lead.phase);
  return (
    <div className="hv-card p-4 md:p-5 space-y-3" data-testid="lead-detail-hero">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#647087]">{lead.external_ref || "Lead"}</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-0.5 leading-tight">
            {lead.title}
          </h1>
          <div className="text-sm text-[#a0aabf] mt-1 flex items-center gap-2 flex-wrap">
            {customer ? (
              <Link
                to={`/customers/${customer.id}`}
                className="text-[#1fb6c1] hover:underline inline-flex items-center gap-1"
                data-testid="lead-detail-customer-link"
              >
                <Building2 className="w-3.5 h-3.5" />
                {customer.name}
                <ExternalLink className="w-3 h-3 opacity-60" />
              </Link>
            ) : (
              <span className="text-[#A78BFA] inline-flex items-center gap-1">
                <Building2 className="w-3.5 h-3.5" /> {lead.customer_name || "Prospect (no customer yet)"}
              </span>
            )}
            {(lead.industry || customer?.industry) && (
              <span className="text-[#647087] truncate">· {lead.industry || customer?.industry}</span>
            )}
            {(lead.province_area || customer?.province_area) && (
              <span className="text-[#647087] inline-flex items-center gap-0.5">
                <MapPin className="w-3 h-3" /> {lead.province_area || customer?.province_area}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <HeatBadge score={lead.heat_score} />
          <span
            className="px-2 py-1 rounded-md border text-[10px] uppercase tracking-wider font-bold"
            style={{ borderColor: `${meta.color}55`, background: `${meta.color}1a`, color: meta.color }}
            data-testid="lead-detail-phase-chip"
          >
            {meta.label}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 pt-1 border-t border-white/5">
        {phone && (
          <ActionTile icon={Phone} label={`Call ${phone}`} href={`tel:${phone}`} testid="lead-action-call" />
        )}
        {phone && (
          <ActionTile
            icon={MessageSquare}
            label="WhatsApp"
            href={`https://wa.me/${(phone || "").replace(/\D/g, "")}`}
            color="#22C55E"
            testid="lead-action-whatsapp"
          />
        )}
        {email && (
          <ActionTile
            icon={Mail}
            label="Email"
            href={`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(email)}`}
            color="#F59E0B"
            testid="lead-action-email"
          />
        )}
        <ActionTile icon={CalendarPlus} label="Meeting" onClick={onOpenMeeting} color="#A78BFA" testid="lead-action-meeting" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 pt-1">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[#647087]">Phase</span>
          <select
            value={lead.phase}
            onChange={(e) => onUpdatePhase(e.target.value)}
            disabled={phaseSaving}
            className="hv-input mt-1 text-xs"
            data-testid="lead-detail-phase-select"
          >
            {LEAD_PHASE_GROUPS.map((g) => (
              <optgroup key={g.group} label={g.group}>
                {g.phases.map((p) => (
                  <option key={p.key} value={p.key}>{p.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        {lead.lead_rating && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#647087]">Rating</div>
            <div className="text-amber-300 inline-flex items-center gap-1 mt-1.5 text-sm">
              <Star className="w-3.5 h-3.5 fill-current" /> {lead.lead_rating} / 5
            </div>
          </div>
        )}
        {lead.price_bracket && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#647087]">Price bracket</div>
            <div className="text-emerald-300 mt-1.5 text-sm">
              {PRICE_BRACKET_LABELS[lead.price_bracket] || lead.price_bracket}
            </div>
          </div>
        )}
        {lead.value > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#647087]">Value (ZAR)</div>
            <div className="text-[#1fb6c1] mt-1.5 text-sm font-mono">
              R{Number(lead.value).toLocaleString("en-GB")}
            </div>
          </div>
        )}
        {lead.lead_source && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#647087]">Source</div>
            <div className="text-[#a0aabf] mt-1.5 text-sm">{lead.lead_source}</div>
          </div>
        )}
        {lead.next_followup_date && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#647087]">Next follow-up</div>
            <div className="text-rose-300 mt-1.5 text-sm">
              {new Date(lead.next_followup_date).toLocaleDateString("en-GB")}
            </div>
          </div>
        )}
        {lead.current_provider && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#647087]">vs.</div>
            <div className="text-amber-300 mt-1.5 text-sm">{lead.current_provider}</div>
          </div>
        )}
      </div>

      {lead.notes && (
        <div className="border border-white/10 rounded-md p-3 bg-white/[0.02] text-sm whitespace-pre-wrap text-[#cbd5e1]">
          {lead.notes}
        </div>
      )}

      {!lead.customer_id && (
        <div className="hv-card border-violet-500/40 bg-violet-500/5 p-3 text-xs text-violet-200 flex items-center justify-between gap-2 flex-wrap">
          <span>This prospect hasn't been promoted to a customer yet — calls & meetings won't sync until you do.</span>
          <button
            type="button"
            onClick={onPromoteToCustomer}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="lead-detail-promote-btn"
          >
            Promote to customer
          </button>
        </div>
      )}
    </div>
  );
}
